import { useQuery } from '@tanstack/react-query'
import { TASK_STATUS_LABELS, type TaskStatus } from '@netcon-work/shared'
import { AufgabenVerweis } from './AufgabeKontext'
import { rueckstandQuery } from '../lib/queries'
import { Card, CardPad, SectionTitle } from './ui/primitives'

/**
 * **Der Rückstand** – was Work führt und das Quellsystem noch nicht weiß (Phase 5, AP 5.5).
 *
 * ## Warum diese Liste überhaupt sichtbar ist
 *
 * Ab Phase 5 verändert Work Daten in fremden Systemen. Die Phasendatei verlangt deshalb
 * einen Trockenlauf – „was würde geschrieben werden" – und diese Liste **ist** er. Sie
 * bleibt auch, wenn der Schreibweg längst läuft: dann ist sie die Vorschau davor und die
 * Antwort auf „warum steht das in GitLab noch anders".
 *
 * ## Die Zeile mit Hindernis ist die wichtigste
 *
 * Eine Abweichung, die **nicht** geschrieben werden kann, verschwindet nirgends – sie
 * steht mit Grund in der Liste. Drei Gründe gibt es heute:
 *
 * - **`zurueckgestellt` hat in GitLab kein Label.** Es gibt nichts zu schreiben, und das
 *   ist kein Fehler, sondern eine Eigenschaft des Zuschnitts.
 * - **Das Projekt ist nicht freigegeben** – die Positivliste aus AP 5.2.
 * - **Die Person hat kein GitLab-Konto**, und das ist bei Work der Normalfall: 9 von 32
 *   ([E-51](../../../../docs/ENTSCHEIDUNGEN.md)). Der steht erst beim Ausführen fest und
 *   erscheint deshalb im Protokoll, nicht hier.
 *
 * Sie stillschweigend zu überspringen wäre die bequeme Variante – und genau die, bei der
 * jemand drei Wochen später fragt, warum eine Aufgabe seit Mai „gleich fertig" ist.
 */
export function Rueckstand() {
  const { data } = useQuery(rueckstandQuery)
  const aufgaben = data?.aufgaben ?? []
  /** Auch hier: wer wissen will, warum etwas offen ist, muss die Aufgabe sehen können. */

  return (
    <>
      <SectionTitle
        hint={
          aufgaben.length === 0
            ? 'Work und die Quellsysteme sagen dasselbe'
            : `${aufgaben.length} ${aufgaben.length === 1 ? 'Aufgabe wartet' : 'Aufgaben warten'} auf das Quellsystem`
        }
      >
        Rückstand
      </SectionTitle>

      {!data?.eingerichtet && (
        <Card className="mb-3">
          <CardPad className="text-[12.5px] text-ink2">
            <strong>Trockenlauf.</strong> Es ist kein Schreibzugang eingerichtet – die Liste zeigt,
            was <em>zu tun wäre</em>. Geschrieben wird nichts.
          </CardPad>
        </Card>
      )}

      {aufgaben.length === 0 ? (
        <Card>
          <CardPad className="text-[12.5px] text-muted">
            Nichts offen – Work und die Quellsysteme sagen dasselbe.
          </CardPad>
        </Card>
      ) : (
        <Card>
          <CardPad>
            <div className="overflow-x-auto">
              <table className="min-w-[560px] w-full border-collapse text-[12px]">
                <thead>
                  <tr>
                    {['Aufgabe', 'was', 'Quelle sagt', 'Work sagt', 'Hindernis'].map((h) => (
                      <th
                        key={h}
                        className="border-b border-line px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-[0.4px] text-muted uppercase"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aufgaben.map((a) => (
                    <tr key={`${a.id}-${a.feld}`}>
                      <td className="border-b border-line px-2 py-1.5">
                        {a.externalUrl ? (
                          <a
                            href={a.externalUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-[11.5px] hover:underline"
                          >
                            {a.externalKey ?? a.id}
                          </a>
                        ) : (
                          <span className="font-mono text-[11.5px]">{a.externalKey ?? a.id}</span>
                        )}
                        <AufgabenVerweis
                          aufgabeId={a.id}
                          className="ml-2 text-left text-ink2 hover:text-accent hover:underline"
                          title={a.title}
                        >
                          {gekuerzt(a.title)}
                        </AufgabenVerweis>
                      </td>
                      <td className="border-b border-line px-2 py-1.5 text-ink2">
                        {FELD_LABELS[a.feld] ?? a.feld}
                      </td>
                      <td className="border-b border-line px-2 py-1.5 text-muted">
                        {wert(a.feld, a.von)}
                      </td>
                      <td className="border-b border-line px-2 py-1.5">
                        <strong>{wert(a.feld, a.nach)}</strong>
                        {a.plan?.label && (
                          <span className="ml-2 font-mono text-[11px] text-muted">
                            {a.plan.label}
                          </span>
                        )}
                      </td>
                      <td className="border-b border-line px-2 py-1.5">
                        {a.hindernis ? (
                          <span className="text-pool-sofort">{a.hindernis}</span>
                        ) : (
                          <span className="text-muted">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardPad>
        </Card>
      )}
    </>
  )
}

/**
 * Lange Titel kürzen – **mit Auslassungszeichen**. Ohne es endet der Titel mitten im Wort
 * und sieht aus wie ein Datenfehler; der volle Text steht im `title`-Attribut.
 */
function gekuerzt(titel: string): string {
  return titel.length > 46 ? `${titel.slice(0, 45)}…` : titel
}

const FELD_LABELS: Record<string, string> = {
  status: 'Status',
  assignee: 'Zuweisung',
  comment: 'Kommentar',
}

/**
 * Statuswerte bekommen ihre deutsche Beschriftung, Personennamen und Kommentartexte
 * stehen so da, wie sie sind – ein Name durch `TASK_STATUS_LABELS` gedreht ergäbe
 * `undefined`, und das sähe im Rückstand aus wie ein Datenfehler.
 */
function wert(feld: string, wert: string | null): string {
  if (wert === null) return 'niemand'
  return feld === 'status' ? (TASK_STATUS_LABELS[wert as TaskStatus] ?? wert) : wert
}
